import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { authAPI, userManagementAPI, setCurrentSiteId } from '../services/api';

/** Public parent packet — token auth only; skip staff auth bootstrap on this path. */
export function isPublicPacketPathname(pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/packet/');
}

/**
 * All fully-public routes (no staff login involved). Auth bootstrap must not run
 * here: a stale access_token would 401 -> refresh-fail -> hard redirect to /login
 * (api.js interceptor), bouncing visitors off public pages like /sms-opt-in.
 */
const PUBLIC_PATHS = ['/privacy', '/messaging-terms', '/sms-opt-in'];

export function isPublicPathname(pathname) {
  if (typeof pathname !== 'string') return false;
  if (isPublicPacketPathname(pathname)) return true;
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function loadPersistedSite() {
  try {
    const raw = localStorage.getItem('current_site');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const isPublicRoute = isPublicPathname(location.pathname);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mfaVerified, setMfaVerified] = useState(() => localStorage.getItem('mfa_verified') === 'true');
  const [allSites, setAllSites] = useState([]);
  const [currentSite, setCurrentSiteState] = useState(loadPersistedSite);

  const mfaRequired = user?.mfa_enabled && !mfaVerified;
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const isAttendanceOnly = user?.role === 'attendance';
  const userSiteIds = user?.sites || [];
  const isSiteBound = !isSuperAdmin && userSiteIds.length > 0;
  const canSwitchSites = isSuperAdmin || (isAdmin && userSiteIds.length > 1);

  const assignedSites = allSites.filter(s => userSiteIds.includes(s.id));
  const visibleSites = isSuperAdmin ? allSites : assignedSites;

  const setCurrentSite = useCallback((site) => {
    setCurrentSiteState(site);
    setCurrentSiteId(site?.id || null);
    if (site) {
      localStorage.setItem('current_site', JSON.stringify(site));
    } else {
      localStorage.removeItem('current_site');
    }
  }, []);

  const applySiteFromUser = useCallback((userData, siteList) => {
    const uSites = userData.sites || [];
    const isSA = userData.role === 'superadmin';

    if (!isSA && uSites.length === 1) {
      const bound = siteList.find(s => s.id === uSites[0]) || { id: uSites[0], name: 'Site', slug: '' };
      setCurrentSite(bound);
      return;
    }

    const persisted = loadPersistedSite();
    if (persisted) {
      const allowed = isSA ? siteList : siteList.filter(s => uSites.includes(s.id));
      if (allowed.some(s => s.id === persisted.id)) {
        setCurrentSite(persisted);
        return;
      }
    }

    if (isSA) {
      setCurrentSite(null);
    } else if (uSites.length > 0) {
      const first = siteList.find(s => s.id === uSites[0]);
      setCurrentSite(first || null);
    } else {
      setCurrentSite(null);
    }
  }, [setCurrentSite]);

  const loadSites = useCallback(async () => {
    try {
      const res = await userManagementAPI.getSites();
      const list = res.data.results || res.data || [];
      setAllSites(list);
      return list;
    } catch {
      return [];
    }
  }, []);

  const redirectToAccessDenied = useCallback((email) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('mfa_verified');
    const q = email ? `?email=${encodeURIComponent(email)}` : '';
    window.location.href = `/access-denied${q}`;
  }, []);

  useEffect(() => {
    if (isPublicRoute) {
      setLoading(false);
      return undefined;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      setLoading(false);
      return undefined;
    }

    if (user) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([authAPI.getCurrentUser(), loadSites()])
      .then(([userData, siteList]) => {
        if (cancelled) return;
        setUser(userData);
        if (!userData.mfa_enabled) {
          setMfaVerified(true);
        }
        applySiteFromUser(userData, siteList);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err.response?.data?.code;
        const detail = err.response?.data?.detail;
        if (code === 'no_group' || code === 'access_denied' || (typeof detail === 'string' && (detail.includes('no_group') || detail.includes('access_denied')))) {
          redirectToAccessDenied(err.response?.data?.email);
          return;
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('mfa_verified');
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isPublicRoute, user, loadSites, applySiteFromUser, redirectToAccessDenied]);

  const login = async (email, password) => {
    try {
      await authAPI.login(email, password);
      const [userData, siteList] = await Promise.all([authAPI.getCurrentUser(), loadSites()]);
      setUser(userData);
      if (!userData.mfa_enabled) {
        setMfaVerified(true);
        localStorage.setItem('mfa_verified', 'true');
      } else {
        setMfaVerified(false);
        localStorage.removeItem('mfa_verified');
      }
      applySiteFromUser(userData, siteList);
      return { success: true, mfaRequired: userData.mfa_enabled };
    } catch (error) {
      const networkUnreachable =
        error.response?.status === 0 ||
        !error.response ||
        (error.message && error.message.toLowerCase().includes('network error'));
      if (networkUnreachable) {
        return {
          success: false,
          error: 'Cannot reach backend. Is it running on http://localhost:8000? (Frontend proxies /api to the backend.)',
        };
      }
      const data = error.response?.data;
      const detail = data?.detail;
      let msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(' ') : null);
      if (!msg && data) msg = typeof data === 'string' ? data : JSON.stringify(data);
      if (!msg) msg = error.message || (error.response?.status === 401 ? 'Invalid email or password.' : (error.response?.status === 403 ? 'Sign-in with password is disabled. Use Sign in with Microsoft.' : 'Login failed'));
      return { success: false, error: msg };
    }
  };

  const logout = useCallback(() => {
    authAPI.logout();
    localStorage.removeItem('mfa_verified');
    localStorage.removeItem('current_site');
    setCurrentSiteId(null);
    setUser(null);
    setMfaVerified(false);
    setCurrentSiteState(null);
    setAllSites([]);
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const [userData, siteList] = await Promise.all([authAPI.getCurrentUser(), loadSites()]);
      setUser(userData);
      if (!userData.mfa_enabled) {
        setMfaVerified(true);
        localStorage.setItem('mfa_verified', 'true');
      }
      applySiteFromUser(userData, siteList);
      return userData;
    } catch (err) {
      const code = err.response?.data?.code;
      const detail = err.response?.data?.detail;
      if (code === 'no_group' || code === 'access_denied' || (typeof detail === 'string' && (detail.includes('no_group') || detail.includes('access_denied')))) {
        redirectToAccessDenied(err.response?.data?.email);
        return;
      }
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('mfa_verified');
      setUser(null);
      setMfaVerified(false);
    }
  }, [applySiteFromUser, loadSites, redirectToAccessDenied]);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, loadUser,
      mfaVerified, setMfaVerified, mfaRequired,
      allSites, visibleSites, currentSite, setCurrentSite,
      isSuperAdmin, isAdmin, isAttendanceOnly, isSiteBound, canSwitchSites,
      userSiteIds, assignedSites,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
